/**
 * Fetching an attachment, and holding it for the life of the document.
 *
 * The Control API authenticates from a header, and neither `<img src>` nor
 * `<a href>` can send one — so the obvious URL answers 401 and a thumbnail
 * renders as its own filename. The bytes are fetched with the session token and
 * handed to elements as an object URL. A token in the query string would work
 * and would put a credential in browser history and in every referrer header.
 *
 * The blob is re-typed from what the *uploader* claimed. The download route
 * refuses to echo a claimed type for anything but a raster image, because
 * serving user-supplied HTML or SVG as its own type from this origin is a
 * cross-site-scripting hole. Re-typing here is safe where serving is not: the
 * document already holds the bytes and it also chooses the element they go
 * into — an SVG reaches an `<img>`, which does not execute script, and a PDF
 * reaches a renderer rather than a frame.
 */

import { useEffect, useState } from 'react';

import { useConsoleConfig } from '../store-context.js';
import { consoleIdToken } from '../../firebase-auth.js';
import type { BoardAttachmentView } from '../../client/types.js';
import { previewKind, type LoadedAttachment } from './preview.js';

/**
 * Digest → bytes, for the life of the document.
 *
 * The bytes behind a digest cannot change, so the cache needs no invalidation.
 * Object URLs are released on unload rather than per component: a thumbnail
 * scrolled out and back would otherwise refetch, and revoking on unmount would
 * break a viewer that is still open.
 */
const cache = new Map<string, Promise<LoadedAttachment>>();

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    for (const pending of cache.values()) {
      void pending.then((loaded) => URL.revokeObjectURL(loaded.url));
    }
    cache.clear();
  });
}

export function useAttachment(attachment: BoardAttachmentView): LoadedAttachment | null {
  const config = useConsoleConfig();
  const [loaded, setLoaded] = useState<LoadedAttachment | null>(null);

  const key = `${attachment.digest}:${attachment.mediaType}`;
  const kind = previewKind(attachment.mediaType, attachment.filename);

  useEffect(() => {
    let live = true;

    let pending = cache.get(key);
    if (pending === undefined) {
      pending = (config.firebase === null ? Promise.resolve(null) : consoleIdToken()).then((firebaseToken) => fetch(`/v1/board/attachments/${encodeURIComponent(attachment.digest)}`, {
        headers: firebaseToken !== null
          ? { Authorization: `Bearer ${firebaseToken}` }
          : config.sessionToken ? { 'x-autonomous-team-session': config.sessionToken } : {},
        credentials: 'same-origin',
      }))
        .then((response) => {
          if (!response.ok) throw new Error(`the file could not be read (${String(response.status)})`);
          return response.blob();
        })
        .then(async (raw) => {
          const blob = raw.type === attachment.mediaType ? raw : new Blob([raw], { type: attachment.mediaType });
          return {
            url: URL.createObjectURL(blob),
            blob,
            // Read once, here: every text preview and the highlighter want it,
            // and `Blob.text()` is a fresh read each time it is called.
            text: kind === 'text' ? await blob.text() : null,
          };
        });
      cache.set(key, pending);
    }

    void pending
      .then((value) => {
        if (live) setLoaded(value);
      })
      .catch(() => {
        // A file that will not load shows as unpreviewable rather than as a
        // broken image; it is still listed, and still named.
      });

    return () => {
      live = false;
    };
  }, [key, attachment.digest, attachment.mediaType, config.sessionToken, config.firebase, kind]);

  return loaded;
}

export { formatBytes, previewKind } from './preview.js';
